import { Box, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart as RechartsChart } from 'recharts';

export interface PieChartData {
  name: string;
  value: number;
  color: string;
  [key: string]: any;
}

export interface PieChartProps {
  data: PieChartData[];
  iconBackgroundOpacity?: number;
  width?: number | 'responsive';
  height?: number | 'responsive';
  innerRadius?: number | 'responsive';
  outerRadius?: number | 'responsive';
  paddingAngle?: number;
  startAngle?: number;
  endAngle?: number;
  renderCustomLabel?: (props: any) => React.ReactElement | null;
  labelType?: 'none' | 'custom' | 'allocation';
  showLabels?: boolean;
  enableShadow?: boolean;
  shadowColor?: string;
  shadowOpacity?: number;
  groupSmallItems?: boolean;
  smallItemThreshold?: number;
}

const colorizeSvg = (svgString: string, color?: string): string => {
  if (!color) return svgString;

  let coloredSvg = svgString.replace(/fill="[^"]*"/g, `fill="${color}"`);
  coloredSvg = coloredSvg.replace(/stroke="[^"]*"/g, `stroke="${color}"`);

  coloredSvg = coloredSvg
    .replace(/width="[^"]*"|height="[^"]*"/g, '')
    .replace(/<svg([^>]*)>/, `<svg$1 width="16px" height="16px">`);

  return coloredSvg;
};

/**
 * PieChart component with automatic grouping of small items
 * 
 * Features:
 * - Automatically groups items with percentage < threshold into "Other" category
 * - Responsive design with different sizes for desktop and mobile
 * - Built-in allocation label renderer with smart positioning
 * - Customizable appearance with shadows and colors
 * - Internationalization support for "Other" label
 * 
 * @param data Array of pie chart data items
 * @param groupSmallItems Whether to group small items (default: true)
 * @param smallItemThreshold Percentage threshold for grouping (default: 3%)
 */
const PieChart: React.FC<PieChartProps> = ({
  data,
  iconBackgroundOpacity = 0.4,
  width = 'responsive',
  height = 'responsive',
  innerRadius = 'responsive',
  outerRadius = 'responsive',
  paddingAngle = 4,
  startAngle = 90,
  endAngle = -360,
  renderCustomLabel,
  labelType = 'none',
  showLabels = true,
  enableShadow = false,
  shadowColor = '#000000',
  shadowOpacity = 0.2,
  groupSmallItems = true,
  smallItemThreshold = 2,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'overview']);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Process data to group small items and sort by value (largest first)
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return data;
    }

    // Calculate total value
    const totalValue = data.reduce((sum, item) => sum + item.value, 0);

    if (totalValue === 0) {
      return data;
    }

    if (!groupSmallItems) {
      // If not grouping small items, just sort by value (largest first)
      const sortedData = [...data].sort((a, b) => b.value - a.value);
      // Filter out items with less than 1% at the end
      return sortedData.filter(item => {
        const percentage = (item.value / totalValue) * 100;
        return percentage >= 1;
      });
    }

    // Separate items into large and small based on percentage
    const largeItems: PieChartData[] = [];
    const smallItems: PieChartData[] = [];

    data.forEach(item => {
      const percentage = (item.value / totalValue) * 100;
      if (percentage >= smallItemThreshold) {
        largeItems.push(item);
      } else {
        smallItems.push(item);
      }
    });

    // If no small items, return sorted large items
    if (smallItems.length === 0) {
      const sortedLarge = largeItems.sort((a, b) => b.value - a.value);
      // Filter out items with less than 1% at the end
      return sortedLarge.filter(item => {
        const percentage = (item.value / totalValue) * 100;
        return percentage >= 1;
      });
    }

    // If only small items, return sorted small items
    if (largeItems.length === 0) {
      const sortedSmall = smallItems.sort((a, b) => b.value - a.value);
      // Filter out items with less than 1% at the end
      return sortedSmall.filter(item => {
        const percentage = (item.value / totalValue) * 100;
        return percentage >= 1;
      });
    }

    // Create "Other" item from small items
    const otherValue = smallItems.reduce((sum, item) => sum + item.value, 0);
    const otherItem: PieChartData = {
      name: t('other'),
      value: otherValue,
      color: '#9C9EA7',
      icon: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="a" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="13" height="13"><path fill="#D9D9D9" d="M0 0h12.5v12.5H0z"/></mask><g mask="url(#a)"><path d="M2.604 10.938q-.43 0-.736-.306a1 1 0 0 1-.306-.736V2.604q0-.43.306-.736.307-.305.736-.306h7.292q.429 0 .736.306.306.307.306.736v7.292q0 .429-.306.736-.306.306-.736.306zm1.563-5.73a.5.5 0 0 0-.371.15q-.15.15-.15.371v2.604q0 .222.15.371.15.15.37.15.222 0 .372-.15t.15-.37V5.728a.5.5 0 0 0-.15-.37.5.5 0 0 0-.371-.15M6.25 3.646a.5.5 0 0 0-.371.15q-.15.15-.15.37v4.167q0 .222.15.371.15.15.371.15t.371-.15.15-.37V4.166a.5.5 0 0 0-.15-.371.5.5 0 0 0-.371-.15M8.333 6.77a.5.5 0 0 0-.37.15q-.15.15-.15.37v1.042q0 .222.15.371.15.15.37.15.222 0 .371-.15.15-.15.15-.37V7.291a.5.5 0 0 0-.15-.371.5.5 0 0 0-.37-.15" fill="#fff"/></g></svg>',
      smallItems: smallItems,
    };

    // Sort large items by value (largest first) and add "Other" item
    const sortedLargeItems = largeItems.sort((a, b) => b.value - a.value);
    const result = [...sortedLargeItems, otherItem];

    // Filter out items with less than 1% at the end
    const finalTotalValue = result.reduce((sum, item) => sum + item.value, 0);
    return result.filter(item => {
      const percentage = (item.value / finalTotalValue) * 100;
      return percentage >= 1;
    });
  }, [data, groupSmallItems, smallItemThreshold, t]);

  // Calculate responsive values with extra space for labels
  const chartWidth = width === 'responsive' ? (isDesktop ? 600 : 450) : width;
  const chartHeight = height === 'responsive' ? (isDesktop ? 400 : 320) : height;
  const chartInnerRadius = innerRadius === 'responsive' ? (isDesktop ? 40 : 50) : innerRadius;
  const chartOuterRadius = outerRadius === 'responsive' ? (isDesktop ? 95 : 100) : outerRadius;

  // Track label positions to prevent overlaps - cleared on each render cycle
  const labelPositionsRef = useRef<Map<string, { y: number; isRightSide: boolean }>>(new Map());
  const renderCycleRef = useRef<string>('');

  // Create a stable key for the current render cycle based on data
  const currentRenderKey = useMemo(() => {
    return processedData?.map(item => `${item.name}-${item.value}`).join('|') || '';
  }, [processedData]);

  // Built-in allocation label renderer
  const renderAllocationLabel = useCallback((props: any) => {
    // Clear positions at the start of each render cycle
    if (renderCycleRef.current !== currentRenderKey) {
      labelPositionsRef.current.clear();
      renderCycleRef.current = currentRenderKey;
    }

    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, payload, fill, percent, index } = props;

    if (!payload?.color) {
      return null;
    }

    const percentage = Math.round(percent * 100);

    const getIconLeftOffset = () => {
      if (percentage === 100) {
        return 64;
      }

      if (percentage < 10) {
        return -52;
      }

      if (percentage >= 10) {
        return -58;
      }

      return 0;
    };

    // Normalize angle to 0-360 range for corner detection
    // Recharts uses negative angles, so we convert: -90° (top) -> 270°, 0° -> 0°, 90° -> 90°
    const normalizedAngle = midAngle < 0 ? 360 + midAngle : midAngle;

    // Detect corners: top (90°), right (0°/360°), bottom (270°), left (180°)
    // Use a tolerance of ±15° to detect near-corner positions
    const cornerTolerance = 15;
    const isNearTop = Math.abs(normalizedAngle - 90) <= cornerTolerance || Math.abs(normalizedAngle - 450) <= cornerTolerance;
    const isNearRight = Math.abs(normalizedAngle - 0) <= cornerTolerance || Math.abs(normalizedAngle - 360) <= cornerTolerance;
    const isNearBottom = Math.abs(normalizedAngle - 270) <= cornerTolerance;
    const isNearLeft = Math.abs(normalizedAngle - 180) <= cornerTolerance;
    const isCorner = isNearTop || isNearRight || isNearBottom || isNearLeft;

    // Calculate line endpoints - start from the middle of the segment's outer edge (at midAngle)
    // Line length is proportional to segment size: larger segments get longer lines
    // Minimum: 20px for very small segments, Maximum: 50px for large segments
    let lineLength = 20 + (index * index / 2 * 8);
    lineLength = Math.min(lineLength, 20) + index + 8;
    if (isNearTop) {
      lineLength = lineLength + 16;
    }

    // Start point at the middle of the segment's outer edge (at outerRadius, using midAngle)
    const midAngleRad = -midAngle * RADIAN;
    const x1 = cx + outerRadius * Math.cos(midAngleRad);
    const y1 = cy + outerRadius * Math.sin(midAngleRad);

    // End point extending outward from the middle of the segment
    const x2 = cx + (outerRadius + lineLength) * Math.cos(midAngleRad);
    let y2 = cy + (outerRadius + lineLength) * Math.sin(midAngleRad);

    // Determine if label should be on left or right
    const isRightSide = x2 > cx;

    // Adjust horizontal line length based on corner position
    let horizontalLineLength = isRightSide ? 20 : 30;
    if (isCorner) {
      // Increase horizontal line length at corners for better spacing
      horizontalLineLength = isRightSide ? 30 : 40;
    }

    const x3 = isRightSide ? x2 + horizontalLineLength : x2 - horizontalLineLength;

    return (
      <g>
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          strokeWidth={1}
          strokeDasharray="3 3"
          stroke={theme.palette.navy[600]}
        />
        <line
          x1={x2}
          y1={y2}
          x2={x3}
          y2={y2}
          strokeWidth={1}
          strokeDasharray="3 3"
          stroke={theme.palette.navy[600]}
        />
        <circle
          cx={x3}
          cy={y2}
          r={3}
          fill={theme.palette.navy[600]}
          stroke="none"
        />
        {payload.icon && payload.color && (
          <foreignObject
            x={x3 + (isRightSide ? 8 : getIconLeftOffset())}
            y={y2 - 9}
            width="21"
            height="21"
            style={{
              padding: '2px',
              borderRadius: '50%',
              backgroundColor: alpha(payload.color, iconBackgroundOpacity || 0.4),
            } as React.CSSProperties}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                color: payload.color,
              }}
              dangerouslySetInnerHTML={{ __html: colorizeSvg(payload.icon, payload.iconColor) }}
            />
          </foreignObject>
        )}
        <text
          x={x3 + (isRightSide ? 32 : -8)}
          y={y2 + 2}
          textAnchor={isRightSide ? 'start' : 'end'}
          dominantBaseline="middle"
          className="text-base font-semibold"
          fontSize="14"
          fill={fill}
          fontWeight="600"
        >
          {`${percentage ?? 0}%`}
        </text>

        {payload.name === t('other') && <text
          x={x3 + (isRightSide ? 8 : 0)}
          y={y2 + 14}
          textAnchor={isRightSide ? 'start' : 'end'}
          fill={theme.palette.text.secondary}
          fontSize="12"
          fontWeight="500"
          dominantBaseline="central"
        >
          {payload.name}
        </text>}
      </g>
    );
  }, [currentRenderKey, theme, t, iconBackgroundOpacity]);

  // Determine which label renderer to use
  const getLabelRenderer = () => {
    if (labelType === 'allocation') {
      return renderAllocationLabel;
    }
    if (labelType === 'custom' && renderCustomLabel) {
      return renderCustomLabel;
    }
    return false;
  };

  return (
    <Box display="flex" justifyContent="center">
      <RechartsChart
        width={chartWidth}
        height={chartHeight}
        margin={{ top: 60, right: 100, bottom: 60, left: 100 }}
      >
        <Pie
          cx="50%"
          cy="50%"
          data={processedData}
          startAngle={startAngle}
          endAngle={endAngle}
          paddingAngle={paddingAngle}
          innerRadius={chartInnerRadius}
          outerRadius={chartOuterRadius}
          labelLine={false}
          label={showLabels ? getLabelRenderer() : false}
          dataKey="value"
        >
          {processedData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color}
              stroke="0"
              style={
                enableShadow
                  ? {
                    filter: `drop-shadow(2px 3px 2px ${alpha(shadowColor, shadowOpacity)})`,
                  }
                  : undefined
              }
            />
          ))}
        </Pie>
      </RechartsChart>
    </Box>
  );
};

export default PieChart; 